// MODELUL INSTALAȚIILOR, cum îl desenează pagina «Instalații».
//
// E documentul soluției `mep` (formatul 3.24 §43: `MepSegment`, `MepEquipment`,
// regulile `INS.*`), adus în memorie de `dinDocument.ts`. Traseele și
// verdictele le face motorul de pe server (`mep/`); aici doar se desenează.
//
// Coordonatele sunt ale planului: după `northUp` și `laOrigineLocala`, adică
// +x est, +y SUD, în metri față de originea locală a desenului — cadrul în care
// stau și `StoreyPlan`-urile. `z` e cota ABSOLUTĂ, în metri față de ±0,00.

import type { Cheie } from "../i18n";

export type Sistem =
  | "apa_rece" | "apa_calda" | "canalizare" | "pluviale"
  | "incendiu" | "electrice" | "incalzire" | "ventilatie";

/** Pe unde trece un segment. Decide verificările (în șapă nu se admit
 *  îmbinări, art. 15.59) și desenul (în șapă se trage punctat). */
export type Mediu = "placare" | "sapa" | "ghena" | "plafon" | "aparent" | "ingropat";

/** Ce rol are segmentul în rețeaua lui: ramura WC-ului e separată de ramura
 *  obiectelor mici (plan §7.1), coloana e verticală, iar apa ajunge la peretele
 *  umed printr-o țeavă întreagă în șapă și se împarte pe perete (plan §7.2).
 *  `lipsa` nu e o țeavă: era traseul-martor al unei piese fără drum, din
 *  documentele mai vechi; motorul nu-l mai scrie — piesa fără scurgere se
 *  arată roșie (`pieseFaraRacord`), nu primește o țeavă. Nu se desenează și
 *  nu intră în cantități. */
export type Rol =
  | "ramura_wc" | "ramura_obiecte" | "racord" | "coloana" | "lipsa"
  | "distributie" | "intrare_sapa" | "tur" | "retur" | "circuit" | "canal" | "bucla"
  | "alimentare" | "principala" | "bransament" | "colector" | "racord_retea" | "ventilatie_primara"
  | "pat";

/** Materialul, ca un cod: numele lui stă în catalog (`inst.material.*`), ca să
 *  se scrie în limba ecranului, iar devizul să-l poată lega de un articol. */
export type Material = "pp" | "pe_x" | "pe_xa" | "ppr" | "tabla_zincata" | "cablu_cyy" | "cablu_tub" | "pat_cabluri";

export type Punct3 = [number, number, number];

export interface Segment {
  id: string;
  sistem: Sistem;
  rol: Rol;
  /** Nivelul pe care stă (`StoreyPlan.id`); coloanele au nivelul de jos. */
  storey: string | null;
  /** Corpul (`Building`): o coloană se vede pe orice nivel prin care trece,
   *  dar numai când corpul ei e desenat — altfel ar pluti lângă alt corp. */
  building?: string | null;
  puncte: Punct3[];
  /** Diametrul nominal, în mm (DN 100, PE-X 20…); la cablul unui
   *  apartament, secțiunea în mm²; 0 la celelalte cabluri. */
  dn: number;
  /** Diametrul exterior desenat, în metri (cu izolația). */
  d_m: number;
  /** Secțiunea dreptunghiulară, la canale de aer și paturi de cabluri. */
  sectiune?: { l: number; h: number };
  /** Direcția în plan a lățimii secțiunii: un pat de cabluri vertical stă
   *  lipit de perete, cu lățimea de-a lungul lui. */
  directie?: [number, number];
  /** Panta, la canalizare (2 % = 0.02). */
  panta?: number;
  mediu: Mediu;
  material: Material;
  /** Apartamentul (`Flat`), când segmentul e al unuia. */
  flat?: string | null;
  /** Piesele (`Furniture`) pe care le deservește: legătura pe care o citesc
   *  verificările, în loc s-o ghicească din geometrie. */
  piese?: string[];
  /** Coloana (id de segment) în care se varsă o ramură de canalizare. */
  spre?: string;
  /** Ghena coloanei: leagă tronsoanele coloanei (câte unul pe etaj) între ele
   *  și de colectorul ei din subsol (`rutaSpreColector`). */
  ghena?: string;
  /** Coturile și ramificațiile de pe segment, deduse de motor din geometrie. */
  fitinguri?: Fiting[];
  /** La cabluri: circuitul din tablou (L1, P2, F1, D1; `comun`, `coloana`),
   *  tipul lui, cablul (`3x2.5`), conductoarele și tubul (Ø mm, tab. 5.7). */
  circuit?: string;
  tip_circuit?: TipCircuit;
  cablu?: string;
  fire?: number;
  tub_mm?: number;
  /** Capetele firului: de unde pleacă (tabloul, doza, aparatul dinainte) și
   *  în ce aparat intră. */
  terminale?: string[];
  /** Articolul de catalog din care e făcut (`mep/catalog/articole.json`),
   *  barele (colacii, tamburele) din care e înșiruit și îmbinările dintre ele. */
  articol?: string;
  bucati?: number;
  imbinari?: number;
}

/** Un articol de catalog, cu proprietățile și limitele lui (din rezumatul
 *  soluției, `summary.articole`): ce se cumpără ca să se execute traseul. */
export interface Articol {
  id: string;
  fel: "teava" | "fiting" | "cablu" | "tub" | "pat" | "echipament";
  nume: { ro: string; en: string };
  unitate: "m" | "buc";
  dn?: number;
  cablu?: string;
  d_mm?: number;
  latime_mm?: number;
  adancime_mm?: number;
  lungime_bara_m?: number;
  lungime_max_m?: number;
  imbinare?: string;
  pret?: { valoare: number; moneda: string; per: string } | null;
}

/** Un rând din lista de cantități pe articol (`summary.cantitati_articole`). */
export interface RandCantitate {
  articol: string;
  nume: { ro: string; en: string };
  unitate: "m" | "buc";
  fel: Articol["fel"];
  lungime_m?: number;
  bucati?: number;
  lungime_bara_m?: number | null;
}

/** Tipul unui circuit electric (I 7-2011 art. 5.4): iluminatul separat de
 *  prize, consumatorii mari pe forță, curenții slabi separat; `comun` e al
 *  spațiilor comune, `coloana` cablul apartamentului de la firidă. */
export type TipCircuit = "lumina" | "prize" | "forta" | "date" | "comun" | "coloana";

/** Un rând din schema tabloului unui apartament (`mep/motor/retele/electrice.py: _rand`). */
export interface Circuit {
  id: string;
  tip: TipCircuit;
  /** Felurile camerelor pe care le servește, în ordinea legării. */
  camere: string[];
  aparate: string[];
  puncte: number;
  putere_w: number;
  sectiune_mm2: number;
  cablu: string;
  tub_mm: number;
  siguranta_a: number;
  rcd_ma: number;
  lungime_m: number;
  cadere_pct: number;
  /** Piesa pe care o servește singur (plita, mașina de spălat…). */
  dedicat?: string | null;
  segmente: string[];
}

/** Un cot sau o ramificație (`mep/motor/fitinguri.py`): pe vârful `i` al
 *  segmentului, cu unghiul lui de catalog (PP: 15°, 30°, 45°, 67°30′, 87°30′;
 *  PE-X: 45°, 90°). `din` e segmentul care intră — la ramificații, și la cotul
 *  dintre două segmente care se continuă —, `dn_intrare` DN-ul lui când e
 *  mai subțire (ramificație redusă, 110/50). */
export interface Fiting {
  i: number;
  fel: "cot" | "ramificatie";
  unghi: number;
  din?: string;
  la?: number;
  dn_intrare?: number;
}

export type FelEchipament =
  | "ghena_propusa" | "contor" | "modul_termic" | "distribuitor" | "tablou" | "doza"
  | "radiator" | "grila" | "piesa_curatire" | "camin_bransament" | "bmpt" | "firida_contorizare"
  | "centrala" | "boiler" | "camin_racord";

export interface Echipament {
  id: string;
  sistem: Sistem;
  fel: FelEchipament;
  storey: string | null;
  building?: string | null;
  /** Centrul cutiei, cota absolută a bazei ei în `z`. */
  centru: Punct3;
  /** Lungime (de-a lungul lui `deg`), lățime, înălțime, în metri. */
  dim: [number, number, number];
  deg?: number;
  flat?: string | null;
  /** La tablou: schema lui, circuitele apartamentului. */
  circuite?: Circuit[];
  articol?: string;
}

export type Verdict = "pass" | "warn" | "fail";

export interface Verificare {
  /** Id-ul permanent al regulii (`INS.SAN.WC_SEPARAT`), din anexa §6.10. */
  regula: string;
  verdict: Verdict;
  /** Ce s-a găsit, în cuvinte; cheia de text și parametrii ei. */
  mesaj: Cheie;
  param?: Record<string, string | number>;
  /** Articolul din normativ: „I 9-2022 art. 18.4”. */
  sursa: string;
  /** Segmentele și echipamentele vinovate, ca să fie evidențiate. */
  elemente: string[];
}

/** O valoare pe care generatorul a folosit-o și de unde a luat-o (plan §2):
 *  ce e implicit sau sugerat se arată „de confirmat”. */
export interface Intrare {
  cheie: Cheie;
  /** Valoarea, ca text de catalog: o mărime (`inst.val.metri`) sau o alegere. */
  valoare: Cheie;
  param?: Record<string, string | number>;
  sursa: "document" | "structura" | "implicit" | "sugerat";
}

/** O piesă sanitară a arhitecturii (`Furniture` de un fel din
 *  `PIESE_SANITARE`), cum stă pe plan: desenată în 3D ca o cutie, ca racordul
 *  din dreptul ei să se vadă unde se termină, nu în aer. */
export interface PiesaSanitara {
  id: string;
  kind: string;
  storey: string | null;
  building?: string | null;
  flat?: string | null;
  centru: [number, number];
  /** Lungime (de-a lungul lui `deg`) și lățime, în metri. */
  dim: [number, number];
  deg: number;
}

/** Felurile de piese pe care le leagă motorul (`mep/profiluri/RO/piese.toml`)
 *  și înălțimea cutiei lor în 3D: cât se vede obiectul de la pardoseală (vasul
 *  WC-ului, buza căzii, blatul lavoarului), ca racordul să intre în el. */
export const PIESE_SANITARE: Readonly<Record<string, number>> = {
  wc: 0.42, wc_suspendat: 0.42, wc_clasic: 0.42, bideu: 0.40,
  lavoar: 0.85, lavoar_dublu: 0.85,
  cada: 0.58, cada_ovala: 0.58, dus: 0.06, dus_deschis: 0.02,
  masina_spalat: 0.85, masina_vase: 0.85, masina_vase_clasic: 0.85,
  chiuveta: 0.90, insula_chiuveta: 0.90,
};

/** Un aparat pus în modul (`MepTerminal`): capătul unui fir, cu felul lui
 *  (priză, întrerupător, plafonieră), ca panoul să spună în ce intră firul. */
export interface Aparat {
  id: string;
  kind: string;
  storey: string | null;
  flat?: string | null;
}

/** Un apartament al arhitecturii (`Flat`), cu conturul lui: instalațiile
 *  fiecăruia se citesc separat, de la racordul lui la ultima piesă. */
export interface Apartament {
  id: string;
  storey: string | null;
  building?: string | null;
  poly: [number, number][];
  /** Identitatea lui prin etaje (`group`), pentru nume. */
  group?: string | null;
}

export interface ModelInstalatii {
  segmente: Segment[];
  echipamente: Echipament[];
  /** Piesele sanitare ale arhitecturii, pentru desen (nu sunt ale motorului). */
  piese: PiesaSanitara[];
  /** Aparatele electrice ale modulelor, pentru nume (nu sunt ale motorului). */
  aparate?: Aparat[];
  /** Apartamentele, cu contururile lor. */
  apartamente?: Apartament[];
  verificari: Verificare[];
  intrari: Intrare[];
  /** Articolele de catalog folosite și lista de cantități pe articol, din
   *  rezumatul soluției. */
  articole?: Record<string, Articol>;
  cantitati_articole?: RandCantitate[];
}

/** Sistemele, în ordinea de așezare la intersecții (I 9-2022 art. 15.37:
 *  întâi ce e gravitațional și gros), cu culoarea lor — aceeași în plan, în
 *  3D și în desenele din documentul de design. */
export const SISTEME: readonly { sistem: Sistem; culoare: string; nume: Cheie }[] = [
  { sistem: "canalizare", culoare: "#8B5A2B", nume: "inst.sistem.canalizare" },
  { sistem: "pluviale", culoare: "#2E8B57", nume: "inst.sistem.pluviale" },
  { sistem: "ventilatie", culoare: "#78909C", nume: "inst.sistem.ventilatie" },
  { sistem: "incalzire", culoare: "#E67E22", nume: "inst.sistem.incalzire" },
  { sistem: "apa_rece", culoare: "#2F6FD6", nume: "inst.sistem.apa_rece" },
  { sistem: "apa_calda", culoare: "#D64545", nume: "inst.sistem.apa_calda" },
  { sistem: "incendiu", culoare: "#B71C1C", nume: "inst.sistem.incendiu" },
  { sistem: "electrice", culoare: "#7E57C2", nume: "inst.sistem.electrice" },
];

export type Categorie = "apa" | "ventilatie" | "electrice" | "incendiu";

/** Un strat al desenului: un sistem, sau — la electrice — un tip de circuit,
 *  fiindcă iluminatul, prizele, forța și curenții slabi se citesc separat pe
 *  planșă (și au fiecare culoarea lor, aceeași ca în editorul de module). */
export type Strat = Sistem | `electrice.${TipCircuit}`;

export const STRATURI_ELECTRICE: readonly { strat: Strat; tip: TipCircuit; culoare: string; nume: Cheie }[] = [
  { strat: "electrice.lumina", tip: "lumina", culoare: "#e8a33d", nume: "inst.circuit.lumina" },
  { strat: "electrice.prize", tip: "prize", culoare: "#3d7fe8", nume: "inst.circuit.prize" },
  { strat: "electrice.forta", tip: "forta", culoare: "#d2453b", nume: "inst.circuit.forta" },
  { strat: "electrice.date", tip: "date", culoare: "#c8a200", nume: "inst.circuit.date" },
  { strat: "electrice.comun", tip: "comun", culoare: "#7E57C2", nume: "inst.circuit.comun" },
  { strat: "electrice.coloana", tip: "coloana", culoare: "#4a3f8f", nume: "inst.circuit.coloana" },
];

/** STRATURILE PE CATEGORII: fiecare categorie se aprinde întreagă, iar
 *  subcategoriile ei una câte una. Tot ce curge prin țevi cu apă — rece,
 *  caldă, canalizare, pluviale, încălzire — stă sub «Apă»; electricele se
 *  împart pe tipuri de circuit. Gruparea e numai a desenului: fiecare sistem
 *  își păstrează regulile lui. */
export const CATEGORII: readonly { categorie: Categorie; nume: Cheie; straturi: readonly Strat[] }[] = [
  { categorie: "apa", nume: "inst.categorie.apa",
    straturi: ["apa_rece", "apa_calda", "canalizare", "pluviale", "incalzire"] },
  { categorie: "ventilatie", nume: "inst.categorie.ventilatie", straturi: ["ventilatie"] },
  { categorie: "electrice", nume: "inst.categorie.electrice", straturi: STRATURI_ELECTRICE.map((x) => x.strat) },
  { categorie: "incendiu", nume: "inst.categorie.incendiu", straturi: ["incendiu"] },
];

export const TOATE_STRATURILE: readonly Strat[] = CATEGORII.flatMap((c) => c.straturi);

export const CULOARE: Record<Sistem, string> = Object.fromEntries(
  SISTEME.map((s) => [s.sistem, s.culoare])) as Record<Sistem, string>;

export const CULOARE_STRAT: Record<string, string> = {
  ...CULOARE, ...Object.fromEntries(STRATURI_ELECTRICE.map((x) => [x.strat, x.culoare])),
};

/** Stratul pe care stă un segment: sistemul lui, sau tipul circuitului la
 *  cabluri (un cablu fără tip — documentele vechi — e al spațiilor comune). */
export const stratul = (s: Pick<Segment, "sistem" | "tip_circuit" | "rol">): Strat =>
  s.sistem === "electrice"
    ? `electrice.${s.tip_circuit ?? (s.rol === "circuit" ? "comun" : "coloana")}`
    : s.sistem;

/** Numele stratului, ca cheie de text. */
export const numeStrat = (strat: Strat): Cheie =>
  STRATURI_ELECTRICE.find((x) => x.strat === strat)?.nume ?? SISTEME.find((x) => x.sistem === strat)!.nume;

/** Culoarea piesei fără scurgere sau fără apă (`INS.SAN.RACORD`): roșu, pe
 *  piesa din plan și pe cutia ei din 3D — nu o țeavă inventată. */
export const CULOARE_LIPSA = "#e53935";

/** Culoarea unui segment: a stratului lui — a sistemului, sau a tipului de
 *  circuit la cabluri (roșu la martorul din documentele vechi). */
export const culoareSegment = (s: Segment): string =>
  (s.rol === "lipsa" ? CULOARE_LIPSA : CULOARE_STRAT[stratul(s)] ?? CULOARE[s.sistem]);

/** Piesele sanitare fără scurgere sau fără apă: cele numite de `INS.SAN.RACORD`
 *  (și cele cu traseu-martor, în documentele mai vechi). Planul și 3D-ul le
 *  arată roșii: lipsa e a arhitecturii (o ghenă lângă piesă), nu a traseului. */
export function pieseFaraRacord(m: ModelInstalatii): Set<string> {
  const out = new Set<string>(m.verificari.find((v) => v.regula === "INS.SAN.RACORD")?.elemente ?? []);
  for (const s of m.segmente) if (s.rol === "lipsa") for (const p of s.piese ?? []) out.add(p);
  return out;
}

/** Lungimea unei polilinii 3D, în metri. */
export function lungime(p: readonly Punct3[]): number {
  let L = 0;
  for (let i = 1; i < p.length; i++) {
    L += Math.hypot(p[i][0] - p[i - 1][0], p[i][1] - p[i - 1][1], p[i][2] - p[i - 1][2]);
  }
  return L;
}

/** Cantitățile pe sistem și diametru (la cabluri: pe cablu, `3x2.5`): metri
 *  liniari, cum intră în deviz. */
export function cantitati(m: ModelInstalatii): { sistem: Sistem; dn: number; cablu?: string; ml: number }[] {
  const acc = new Map<string, { sistem: Sistem; dn: number; cablu?: string; ml: number }>();
  for (const s of m.segmente) {
    if (s.rol === "lipsa" || s.rol === "pat") continue;     // martorul nu se cumpără; patul are lista lui
    const k = `${s.sistem}|${s.dn}|${s.cablu ?? ""}`;
    const e = acc.get(k) ?? { sistem: s.sistem, dn: s.dn, ...(s.cablu ? { cablu: s.cablu } : {}), ml: 0 };
    e.ml += lungime(s.puncte);
    acc.set(k, e);
  }
  const ordine = SISTEME.map((s) => s.sistem);
  return [...acc.values()].sort((a, b) =>
    ordine.indexOf(a.sistem) - ordine.indexOf(b.sistem) || b.dn - a.dn || (a.cablu ?? "").localeCompare(b.cablu ?? ""));
}

/** Tuburile de protecție ale cablurilor, pe diametru: metri de tub (cablurile
 *  paralele pe același perete au fiecare tubul lor). */
export function cantitatiTuburi(m: ModelInstalatii): { tub_mm: number; ml: number }[] {
  const acc = new Map<number, number>();
  for (const s of m.segmente) {
    if (s.sistem !== "electrice" || !s.tub_mm) continue;
    acc.set(s.tub_mm, (acc.get(s.tub_mm) ?? 0) + lungime(s.puncte));
  }
  return [...acc].map(([tub_mm, ml]) => ({ tub_mm, ml })).sort((a, b) => a.tub_mm - b.tub_mm);
}

/** Paturile de cabluri, pe lățime: metri de pat (jgheab) pe care stau
 *  cablurile apartamentelor, de la firidă la coloană și pe ea în sus. */
export function cantitatiPaturi(m: ModelInstalatii): { latime_mm: number; ml: number }[] {
  const acc = new Map<number, number>();
  for (const s of m.segmente) {
    if (s.rol !== "pat" || !s.sectiune) continue;
    const l = Math.round(s.sectiune.l * 1000);
    acc.set(l, (acc.get(l) ?? 0) + lungime(s.puncte));
  }
  return [...acc].map(([latime_mm, ml]) => ({ latime_mm, ml })).sort((a, b) => a.latime_mm - b.latime_mm);
}

/** DECALAJUL cablurilor paralele: mai multe circuite pe aceeași bucată de
 *  perete (sau aceeași coborâre) stau în model pe axa lui, deci unul peste
 *  altul. Pe planșă tuburile lor stau unul lângă altul: fiecare circuit
 *  primește, pe fiecare picior, un loc în șirul circuitelor care împart acel
 *  picior (în ordinea id-urilor, ca același circuit să-și țină locul de-a
 *  lungul peretelui) și e desenat deplasat perpendicular pe picior cu
 *  `loc × (tub + 4 mm)`, centrat. Întoarce, pe segment, decalajul în plan
 *  al fiecărui picior (`dx, dy` în metri), sau nimic unde e singur. */
export function decalaje(segmente: readonly Segment[]): Map<string, [number, number][]> {
  const cheie = (p: Punct3, q: Punct3) => {
    const a = [p[0].toFixed(2), p[1].toFixed(2), p[2].toFixed(2)].join(",");
    const b = [q[0].toFixed(2), q[1].toFixed(2), q[2].toFixed(2)].join(",");
    return a < b ? `${a}|${b}` : `${b}|${a}`;
  };
  const impart = new Map<string, Set<string>>();
  // circuitul e al apartamentului lui: L1 din două apartamente vecine, pe
  // peretele dintre ele, sunt două cabluri; patul nu e cablu
  const al = (s: Segment) => `${s.flat ?? ""}|${s.circuit ?? s.id}`;
  const cabluri = segmente.filter((s) => s.sistem === "electrice" && s.rol !== "lipsa" && s.rol !== "pat");
  for (const s of cabluri) {
    for (let i = 1; i < s.puncte.length; i++) {
      const k = cheie(s.puncte[i - 1], s.puncte[i]);
      const set = impart.get(k) ?? new Set<string>();
      set.add(al(s));
      impart.set(k, set);
    }
  }
  const out = new Map<string, [number, number][]>();
  for (const s of cabluri) {
    const pas = (s.tub_mm ?? 16) / 1000 + 0.004;
    const dec: [number, number][] = [];
    let singur = true;
    for (let i = 1; i < s.puncte.length; i++) {
      const p = s.puncte[i - 1], q = s.puncte[i];
      const toate = [...(impart.get(cheie(p, q)) ?? [])].sort();
      const loc = toate.indexOf(al(s));
      const n = toate.length;
      if (n <= 1 || loc < 0) { dec.push([0, 0]); continue; }
      singur = false;
      // normala piciorului în plan; la o coborâre (verticală), a piciorului
      // orizontal vecin — sau est, dacă n-are
      let dx = q[0] - p[0], dy = q[1] - p[1];
      if (Math.hypot(dx, dy) < 1e-6) {
        const v = s.puncte[i + 1] ?? s.puncte[i - 2];
        if (v) { dx = v[0] - p[0]; dy = v[1] - p[1]; }
        if (Math.hypot(dx, dy) < 1e-6) { dx = 1; dy = 0; }
      }
      const L = Math.hypot(dx, dy);
      const f = (loc - (n - 1) / 2) * pas;
      dec.push([(-dy / L) * f, (dx / L) * f]);
    }
    if (!singur) out.set(s.id, dec);
  }
  return out;
}

export interface CantitateFiting {
  sistem: Sistem; fel: Fiting["fel"]; dn: number; unghi: number; dn_intrare?: number; bucati: number;
}

/** Fitingurile, numărate pe (sistem, fel, DN, unghi, DN-ul intrării): coturile
 *  și ramificațiile de cumpărat, lângă metrii de țeavă. */
export function cantitatiFitinguri(m: ModelInstalatii): CantitateFiting[] {
  const acc = new Map<string, CantitateFiting>();
  for (const s of m.segmente) {
    for (const f of s.fitinguri ?? []) {
      const k = `${s.sistem}|${f.fel}|${s.dn}|${f.unghi}|${f.dn_intrare ?? ""}`;
      const e = acc.get(k) ?? { sistem: s.sistem, fel: f.fel, dn: s.dn, unghi: f.unghi,
                                ...(f.dn_intrare ? { dn_intrare: f.dn_intrare } : {}), bucati: 0 };
      e.bucati += 1;
      acc.set(k, e);
    }
  }
  const ordine = SISTEME.map((s) => s.sistem);
  return [...acc.values()].sort((a, b) =>
    ordine.indexOf(a.sistem) - ordine.indexOf(b.sistem) || a.fel.localeCompare(b.fel) || b.dn - a.dn
    || a.unghi - b.unghi);
}

const zMin = (s: Segment) => Math.min(...s.puncte.map((p) => p[2]));

/** Cât de aproape trebuie să fie un capăt de altă țeavă ca să fie legat de ea. */
const LEGAT_M = 0.03;

/** Rangul rolurilor pe drumul apei de la piesă spre oraș: pasul «în amonte»
 *  alege, dintre vecinele geometrice, pe cea cu rangul mai mare. */
const AMONTE_APA: Record<string, number> = {
  racord: 0, distributie: 1, intrare_sapa: 2, alimentare: 3, coloana: 4, principala: 5, bransament: 6,
};

/** RUTA unui element până la SURSA lui, în sens invers curgerii — «de unde
 *  vine». La cabluri, pe `terminale`: firul care intră în capătul de pornire
 *  al firului ales, până la tablou, apoi cablul apartamentului bucată cu
 *  bucată (`spre`: pe palier, pe coloană, pe tavanul parterului) până la
 *  branșament; circuitele spațiilor comune, din doza lor pe cablul comun. La apă, pe atingere: racordul → banda pe care stă → țeava din
 *  șapă → cablul… adică țeava apartamentului → coloana de apă → conducta
 *  principală → branșament, mereu spre rolul mai «din amonte». Inginerul vede
 *  dintr-un clic tot drumul de la firidă sau de la contor până la aparat. */
export function rutaSpreSursa(m: ModelInstalatii, id: string): string[] {
  const dupa = new Map(m.segmente.map((s) => [s.id, s]));
  const s0 = dupa.get(id);
  if (!s0) return [];
  const out: string[] = [];
  const vazut = new Set<string>();
  let s: Segment | undefined = s0;
  while (s && !vazut.has(s.id)) {
    vazut.add(s.id);
    out.push(s.id);
    s = s.sistem === "electrice" ? amonteCablu(m, dupa, s, vazut) : amonteApa(m, dupa, s, vazut);
  }
  return out;
}

function amonteCablu(m: ModelInstalatii, dupa: Map<string, Segment>, s: Segment, vazut: Set<string>): Segment | undefined {
  if (s.rol === "circuit" && s.terminale?.length) {
    const de_la = s.terminale[0];
    // firul care intră în capătul de pornire (doză, aparat); de la tablou, cablul apartamentului
    const fir = m.segmente.find((x) => x.sistem === "electrice" && x.rol === "circuit" && x.flat === s.flat
      && x.terminale?.[x.terminale.length - 1] === de_la && !vazut.has(x.id));
    if (fir) return fir;
    const alim = m.segmente.find((x) => x.sistem === "electrice" && x.rol === "alimentare" && x.flat === s.flat
      && x.terminale?.includes(de_la) && !vazut.has(x.id));
    if (alim) return alim;
  }
  return amonteLegat(m, dupa, s, vazut);
}

function amonteApa(m: ModelInstalatii, dupa: Map<string, Segment>, s: Segment, vazut: Set<string>): Segment | undefined {
  // banda spune de unde vine (`spre`: trunchiul ramurii, urcarea din șapă a trunchiului)
  if (s.spre && dupa.get(s.spre) && !vazut.has(s.spre)) return dupa.get(s.spre);
  const rang = AMONTE_APA[s.rol] ?? -1;
  const cap = s.puncte[0];
  // vecinele pe care stă capătul de pornire, cu rolul mai din amonte (sau același, la benzile înlănțuite)
  const vecine = m.segmente.filter((x) => x.sistem === s.sistem && x.id !== s.id && !vazut.has(x.id)
    && (AMONTE_APA[x.rol] ?? -1) >= rang && (x.flat == null || x.flat === s.flat || x.rol !== s.rol)
    && (distPolilinie3(cap, x.puncte) <= LEGAT_M || distPolilinie3(s.puncte[s.puncte.length - 1], x.puncte) <= LEGAT_M));
  if (vecine.length) return vecine.sort((a, b) => (AMONTE_APA[b.rol] ?? -1) - (AMONTE_APA[a.rol] ?? -1))[0];
  return amonteLegat(m, dupa, s, vazut);
}

/** Pasul comun de la rețeaua apartamentului la a clădirii: `spre` (coloana în
 *  care intră), tronsoanele coloanei de dedesubt (prin `ghena`), conducta de
 *  la baza ei (`principala`), branșamentul. */
function amonteLegat(m: ModelInstalatii, dupa: Map<string, Segment>, s: Segment, vazut: Set<string>): Segment | undefined {
  if (s.spre && dupa.get(s.spre) && !vazut.has(s.spre)) return dupa.get(s.spre);
  if (s.rol === "coloana") {
    const jos = m.segmente.filter((t) => t.rol === "coloana" && t.sistem === s.sistem && !!s.ghena && t.ghena === s.ghena
      && zMin(t) < zMin(s) && !vazut.has(t.id)).sort((a, b) => zMin(b) - zMin(a));
    if (jos.length) return jos[0];
    const baza = s.puncte[0];
    return m.segmente.find((t) => ["principala", "bransament"].includes(t.rol) && t.sistem === s.sistem
      && !vazut.has(t.id) && distPolilinie3(baza, t.puncte) <= 0.3);
  }
  if (s.rol === "principala") {
    return m.segmente.find((t) => t.rol === "bransament" && t.sistem === s.sistem && !vazut.has(t.id)
      && (distPolilinie3(s.puncte[0], t.puncte) <= 0.3 || distPolilinie3(s.puncte[s.puncte.length - 1], t.puncte) <= 0.3));
  }
  return undefined;
}

/** Distanța în spațiu de la un punct la o polilinie. */
function distPolilinie3(p: Punct3, pts: readonly Punct3[]): number {
  let best = Infinity;
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i];
    const d = [b[0] - a[0], b[1] - a[1], b[2] - a[2]];
    const l2 = d[0] * d[0] + d[1] * d[1] + d[2] * d[2];
    const t = l2 < 1e-12 ? 0 : Math.max(0, Math.min(1, ((p[0] - a[0]) * d[0] + (p[1] - a[1]) * d[1] + (p[2] - a[2]) * d[2]) / l2));
    best = Math.min(best, Math.hypot(p[0] - (a[0] + d[0] * t), p[1] - (a[1] + d[1] * t), p[2] - (a[2] + d[2] * t)));
  }
  return best;
}

/** Distanța în plan de la un punct la o polilinie. */
function distPolilinie(p: Punct3, pts: readonly Punct3[]): number {
  let best = Infinity;
  for (let i = 1; i < pts.length; i++) {
    const [ax, ay] = pts[i - 1], [bx, by] = pts[i];
    const dx = bx - ax, dy = by - ay, l2 = dx * dx + dy * dy;
    const t = l2 < 1e-12 ? 0 : Math.max(0, Math.min(1, ((p[0] - ax) * dx + (p[1] - ay) * dy) / l2));
    best = Math.min(best, Math.hypot(p[0] - (ax + dx * t), p[1] - (ay + dy * t)));
  }
  return best;
}

/** RUTA unui segment până la rețeaua străzii, în sensul curgerii: ramura →
 *  coloana ghenei ei pe etajul lui → tronsoanele coloanei de dedesubt →
 *  colectorul ghenei din subsol → părinții lui → racordul la stradă. Pe `spre`
 *  unde motorul l-a scris; coloanele (câte un tronson pe etaj) și colectorul
 *  lor se leagă prin `ghena` comună, iar o coloană fără colector (apa) prin
 *  conducta pe care stă baza ei. Inginerul o selectează întreagă dintr-un clic. */
export function rutaSpreColector(m: ModelInstalatii, id: string): string[] {
  const dupa = new Map(m.segmente.map((s) => [s.id, s]));
  const out: string[] = [];
  const vazut = new Set<string>();
  let s: Segment | undefined = dupa.get(id);
  while (s && !vazut.has(s.id)) {
    vazut.add(s.id);
    out.push(s.id);
    if (s.rol === "coloana" || s.rol === "ventilatie_primara") {
      const sis = s.sistem, gh = s.ghena, z = zMin(s);
      const jos = m.segmente
        .filter((t) => t.rol === "coloana" && t.sistem === sis && !!gh && t.ghena === gh && zMin(t) < z)
        .sort((a, b) => zMin(b) - zMin(a));
      for (const t of jos) if (!vazut.has(t.id)) { vazut.add(t.id); out.push(t.id); }
      const baza: Punct3 = jos.length ? jos[jos.length - 1].puncte[0] : s.puncte[0];
      s = m.segmente.find((t) => t.rol === "colector" && t.sistem === sis && !!gh && t.ghena === gh)
        ?? m.segmente.find((t) => ["colector", "principala"].includes(t.rol) && t.sistem === sis
                                  && !vazut.has(t.id) && distPolilinie(baza, t.puncte) < 0.3);
      continue;
    }
    s = s.spre ? dupa.get(s.spre) : undefined;
  }
  return out;
}
